import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Image,
  TouchableWithoutFeedback,
  ScrollView,
} from 'react-native';
import { Text } from 'exoflex';
import { useHistory } from 'react-router';

import { Button, Icon, ToastProvider } from '../core-ui';
import { DRAWER, PAGE, TEXT, ICONS } from '../constants/colors';
import { clientItems, pmItems } from '../data/drawerItems';

import { insightsLogo } from '../../assets/';
import { getRole, removeToken, removeRole, getToken } from '../helpers/storage';

type DrawerItemProps = {
  onPress: () => void;
  active: boolean;
  title: string;
  icon: string;
};

function DrawerItem(props: DrawerItemProps) {
  let { active, title, icon, onPress } = props;

  return (
    <TouchableWithoutFeedback onPress={onPress}>
      <View
        style={[
          styles.drawerItemContainer,
          active && { backgroundColor: DRAWER.renderItem.selected },
        ]}
      >
        <View style={styles.drawerItemLogoContainer}>
          <Icon
            name={icon}
            color={active ? ICONS.selected : ICONS.unselected}
            iconStyle={{ margin: 0 }}
          />
        </View>
        <View style={styles.drawerItemTextContainer}>
          <Text
            weight="medium"
            style={[
              styles.drawerItemText,
              {
                color: active ? TEXT.black : TEXT.blackOpacity,
              },
            ]}
          >
            {title}
          </Text>
        </View>
      </View>
    </TouchableWithoutFeedback>
  );
}

function Drawer() {
  let [role, setRole] = useState('');
  let routerHistory = useHistory();

  useEffect(() => {
    let USER_ROLE = async () => {
      let role = await getRole();
      setRole(role || '');
    };
    USER_ROLE();

    getToken().then((token) => {
      if (!token) {
        routerHistory.push('/');
      }
    });
  }, []);

  let drawerItems;
  if (role === 'USER') {
    drawerItems = clientItems;
  }
  if (role === 'ADMIN') {
    drawerItems = pmItems;
  }

  let [selectedIndex, setSelectedIndex] = useState(0);
  let RenderScene = drawerItems
    ? drawerItems[selectedIndex].scene
    : () => <></>;
  let logOutPress = () => {
    removeToken();
    removeRole();
    routerHistory.push('/');
  };

  return (
    <View style={styles.container}>
      <View style={styles.sidebarContainer}>
        <Image resizeMode="contain" source={insightsLogo} style={styles.logo} />

        {drawerItems &&
          drawerItems.map((item, i) => {
            let { title, icon } = item;
            return (
              <DrawerItem
                key={i}
                active={i === selectedIndex}
                title={title}
                icon={icon}
                onPress={() => setSelectedIndex(i)}
              />
            );
          })}

        <Button
          buttonMode="invisible"
          icon={() => <Icon name="logout" color={TEXT.blackOpacity} />}
          title="Log Out"
          labelStyle={styles.logoutText}
          onPress={logOutPress}
          style={styles.logoutContainer}
        />
      </View>

      <View style={styles.renderContainer}>
        <ToastProvider>
          <ScrollView contentContainerStyle={styles.scrollViewContainer}>
            <RenderScene />
          </ScrollView>
        </ToastProvider>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100vh',
    flexDirection: 'row',
  },
  sidebarContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 32,
  },
  renderContainer: {
    flex: 4,
    backgroundColor: PAGE.default,
  },
  logo: {
    width: 109,
    height: 28,
    marginLeft: 16,
    marginBottom: 32,
  },
  drawerItemContainer: {
    flexDirection: 'row',
    backgroundColor: 'transparent',
    borderRadius: 8,
  },
  drawerItemLogoContainer: {
    padding: 16,
    alignItems: 'center',
  },
  drawerItemTextContainer: {
    justifyContent: 'center',
  },
  drawerItemText: {
    fontSize: 16,
  },
  scrollViewContainer: {
    flex: 1,
    padding: 56,
    backgroundColor: PAGE.default,
  },
  logoutText: {
    color: TEXT.blackOpacity,
    marginLeft: 16,
  },
  logoutContainer: {
    position: 'absolute',
    bottom: 34,
  },
});

export default Drawer;
